/**
 * Ежедневные задания: три ящика, прогресс и награда (§22.3).
 */

import { formatCrystals, formatDuration, type LocaleCode } from "@hive/i18n";
import { useTranslation } from "react-i18next";

import type { DailyQuestView } from "../api/quests.js";
import { Crystals } from "./CrystalIcon.jsx";
import "./quests/quests.css";

/**
 * Картинки ящиков подобраны по виду задания и лежат в `public/art`. Путь
 * собирается по таблице, а не склейкой строки: вид приходит с сервера, и
 * подставлять его в адрес напрямую нельзя.
 */
const BOX_ART: Record<string, string> = {
  road: "/art/box-road.webp",
  household: "/art/box-household.webp",
  yard: "/art/box-yard.webp",
};

export interface QuestScreenProps {
  readonly locale: LocaleCode;
  readonly ecoDayEndsAt: string;
  readonly quests: readonly DailyQuestView[];
  readonly loading?: boolean;
  readonly busy?: boolean;
  readonly onClose: () => void;
  readonly onClaim: (slot: number) => void;
}

export function QuestScreen({
  locale,
  ecoDayEndsAt,
  quests,
  loading = false,
  busy = false,
  onClose,
  onClaim,
}: QuestScreenProps): React.JSX.Element {
  const { t } = useTranslation(["quests", "common"]);

  const msLeft = Math.max(0, new Date(ecoDayEndsAt).getTime() - Date.now());

  const renderQuest = (quest: DailyQuestView): React.JSX.Element => {
    const crystals = formatCrystals(locale, quest.rewardCrystals * 1000, "hive");
    const art = BOX_ART[quest.box];
    // Цель у задания всегда больше нуля, но полоса не должна зависеть от данных
    const fill = quest.target > 0 ? Math.min(1, quest.progress / quest.target) : 0;
    const state = quest.claimed ? " quest-item--done" : quest.claimable ? " quest-item--ready" : "";

    return (
      <li key={quest.slot} className={`quest-item${state}`}>
        {art !== undefined && (
          <img
            className="quest-item__art"
            src={art}
            alt=""
            width={192}
            height={192}
            loading="lazy"
            decoding="async"
          />
        )}

        <div className="quest-item__head">
          <p className="quest-item__box">{t(`quests:box.${quest.box}`)}</p>
          <p className="quest-item__title">
            {t(`quests:daily.${quest.questId}`, { target: quest.target })}
          </p>
        </div>

        <div className="quest-item__bar">
          <span className="quest-item__track" aria-hidden="true">
            <span className="quest-item__fill" style={{ "--fill": fill } as React.CSSProperties} />
          </span>
          <p className="quest-item__progress">
            {t("quests:progress", { current: quest.progress, target: quest.target })}
          </p>
        </div>

        <p className="quest-item__reward">
          <Crystals>{t("quests:reward", { crystals: crystals, xp: quest.rewardXp })}</Crystals>
        </p>

        {quest.claimed ? (
          <p className="quest-item__done">{t("quests:state.claimed")}</p>
        ) : quest.claimable ? (
          <button
            type="button"
            className="button button--primary"
            disabled={busy}
            onClick={() => onClaim(quest.slot)}
          >
            {t("quests:action.claim")}
          </button>
        ) : null}
      </li>
    );
  };

  return (
    <section className="quest-screen" aria-label={t("quests:screen.title")}>
      <header className="quest-screen__head">
        <div>
          <h2 className="quest-screen__title">{t("quests:screen.title")}</h2>
          <p className="quest-screen__reset">
            {t("quests:screen.resets")} · {formatDuration(locale, msLeft)}
          </p>
        </div>
        <button type="button" className="button" onClick={onClose}>
          {t("common:action.close")}
        </button>
      </header>

      {loading ? (
        <p className="quest-screen__state">{t("common:state.loading")}</p>
      ) : (
        <ul className="quest-screen__list">{quests.map(renderQuest)}</ul>
      )}
    </section>
  );
}
